import { useQuery } from '@tanstack/react-query'
import type { GuestFile, ShareLinkAccessOutput, StreamTokenOutput } from '@terafile/shared'
import { useEffect, useRef, useState } from 'react'
import {
  FETCH_ERROR_FALLBACK,
  SUBMIT_ERROR_FALLBACK,
  USER_MESSAGES,
  toUserMessage,
} from '../lib/error-copy.js'
import { type FileKind, classifyFile, iconForKind, isPreviewable } from '../lib/file-kind.js'
import { LOADING, SHARE_PERMISSION_LABEL, STREAM_STATUS_LABEL } from '../lib/labels.js'
import {
  type GuestApiError,
  getGuestDownloadUrl,
  getGuestPreviewUrl,
  getGuestStreamToken,
  listGuestFiles,
  postGuestPlaybackEvent,
} from '../lib/share-links-api.js'
import { GuestUpload } from './GuestUpload.js'
import { ModalOverlay } from './ModalOverlay.js'
import { Watermark } from './Watermark.js'

type Props = {
  token: string
  session: ShareLinkAccessOutput
}

/**
 * ゲスト向けのファイル一覧 + プレイヤー + DL/UL UI。
 *
 * - permission=VIEW:     プレイヤーのみ。DL不可
 * - permission=DOWNLOAD: + 高画質DLボタン
 * - permission=UPLOAD:   + ULコンポーネント
 */
export function GuestFileViewer({ token, session }: Props) {
  const guestToken = session.guestToken
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['guest-files', token],
    queryFn: () => listGuestFiles(token, guestToken),
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (f) => f.streamStatus === 'PENDING' || f.streamStatus === 'QUEUED',
      )
        ? 5000
        : false,
  })

  // 動画再生用（VideoPlayer 展開）
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // 画像 / PDF フルプレビュー対象
  const [previewTarget, setPreviewTarget] = useState<GuestFile | null>(null)

  if (isLoading) {
    return (
      <CenterLayout>
        <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>
      </CenterLayout>
    )
  }
  if (error) {
    const msg = toUserMessage(error, FETCH_ERROR_FALLBACK)
    return (
      <CenterLayout>
        <p className="text-sm text-red-600">{msg}</p>
      </CenterLayout>
    )
  }

  const items = data?.items ?? []
  const selected = selectedId ? items.find((f) => f.id === selectedId) : null

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <h1 className="text-base font-semibold">{session.projectName}</h1>
          <p className="text-xs text-muted-foreground">
            権限: {permissionLabel(session.permission)}
          </p>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* 運用ポリシー注記（F-PREV-12 後段） */}
        <div className="text-[11px] text-muted-foreground/80 border border-border rounded-md p-3 bg-muted/30">
          ⚠ 動画・画像には視聴者識別の透かしが表示されます。
          スクリーンキャプチャ等での流出は完全に防止できないため、
          配布物としての二次利用は控えてください。
        </div>

        {/* 動画再生はモーダルで（インライン展開はファイル数が多いとスクロール戻しが発生し UX が悪い、issue #33） */}
        {selected?.mimeType.startsWith('video/') && (
          <GuestVideoPlayerModal
            token={token}
            guestToken={guestToken}
            file={selected}
            onClose={() => setSelectedId(null)}
          />
        )}

        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">ファイルはまだありません</p>
        ) : (
          <ul className="space-y-2">
            {items.map((file) => (
              <GuestFileRow
                key={file.id}
                file={file}
                token={token}
                guestToken={guestToken}
                permission={session.permission}
                isSelected={file.id === selectedId}
                onPlayVideo={() => setSelectedId(file.id)}
                onPreview={() => setPreviewTarget(file)}
              />
            ))}
          </ul>
        )}

        {session.permission === 'UPLOAD' && (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold">アップロード</h2>
            <GuestUpload token={token} guestToken={guestToken} onUploaded={() => void refetch()} />
          </section>
        )}
      </main>

      {/* 画像 / PDF フルプレビュー */}
      <GuestPreviewModal
        token={token}
        guestToken={guestToken}
        file={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />
    </div>
  )
}

function GuestFileRow({
  file,
  token,
  guestToken,
  permission,
  isSelected,
  onPlayVideo,
  onPreview,
}: {
  file: GuestFile
  token: string
  guestToken: string
  permission: ShareLinkAccessOutput['permission']
  isSelected: boolean
  /** 動画再生（VideoPlayer 展開） */
  onPlayVideo: () => void
  /** 画像 / PDF のフルプレビュー モーダル */
  onPreview: () => void
}) {
  const kind = classifyFile(file.mimeType)
  const isVideo = kind === 'video'
  const canPlay = isVideo && file.streamStatus === 'READY'
  const canPreview = isPreviewable(kind) // image / pdf
  const canDownload = permission !== 'VIEW'

  const onDownload = async () => {
    try {
      const { url } = await getGuestDownloadUrl(token, file.id, guestToken)
      window.location.href = url
    } catch (err) {
      const msg = toUserMessage(err, SUBMIT_ERROR_FALLBACK)
      alert(msg)
    }
  }

  return (
    <li
      className={`border rounded-md p-3 bg-background flex items-center gap-3 ${
        isSelected ? 'border-primary' : 'border-border'
      }`}
    >
      <GuestThumbnail
        token={token}
        guestToken={guestToken}
        file={file}
        kind={kind}
        canPlay={canPlay}
        canPreview={canPreview}
        onClick={canPlay ? onPlayVideo : canPreview ? onPreview : null}
      />
      <div className="flex-1 min-w-0">
        {canPreview ? (
          <button
            type="button"
            onClick={onPreview}
            className="font-medium truncate block text-left hover:underline underline-offset-4 decoration-foreground/30"
          >
            {file.originalName}
          </button>
        ) : (
          <span className="font-medium truncate block">{file.originalName}</span>
        )}
        <span className="text-xs text-muted-foreground">
          {formatBytes(Number(file.sizeBytes))}
          {isVideo && file.streamStatus !== 'READY' && (
            <> · {streamStatusLabel(file.streamStatus)}</>
          )}
        </span>
      </div>
      {isVideo && (
        <button
          type="button"
          onClick={onPlayVideo}
          disabled={!canPlay}
          className="px-3 py-1.5 border border-border rounded-md text-sm font-medium disabled:opacity-50"
        >
          再生
        </button>
      )}
      {canPreview && (
        <button
          type="button"
          onClick={onPreview}
          className="px-3 py-1.5 border border-border rounded-md text-sm font-medium"
        >
          プレビュー
        </button>
      )}
      {canDownload && (
        <button
          type="button"
          onClick={onDownload}
          className="px-3 py-1.5 border border-border rounded-md text-sm font-medium"
        >
          高画質でダウンロード
        </button>
      )}
    </li>
  )
}

/**
 * ゲスト用サムネイル表示。
 *  - 動画 (READY): Stream の公開サムネ URL（バックエンドが file.streamUid から組立）
 *  - 画像: ゲスト inline 署名 URL を `<img>` で lazy 読み込み
 *  - PDF / 音声 / その他: アイコン
 */
function GuestThumbnail({
  token,
  guestToken,
  file,
  kind,
  canPlay,
  canPreview,
  onClick,
}: {
  token: string
  guestToken: string
  file: GuestFile
  kind: FileKind
  canPlay: boolean
  canPreview: boolean
  onClick: (() => void) | null
}) {
  const isImage = kind === 'image'
  const { data: previewData } = useQuery<{ url: string; expiresAt: string }, GuestApiError>({
    queryKey: ['guest-preview-url', token, file.id],
    queryFn: () => getGuestPreviewUrl(token, file.id, guestToken),
    enabled: isImage,
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  })

  const wrapperBase =
    'w-20 h-14 rounded-md overflow-hidden flex-shrink-0 relative ring-1 transition-all'
  const overlay = canPlay ? '▶' : canPreview ? '🔍' : null

  const inner = (
    <>
      {isImage && previewData?.url ? (
        <img
          src={previewData.url}
          alt=""
          className="w-full h-full object-cover bg-muted"
          loading="lazy"
        />
      ) : kind === 'video' && file.thumbnailUrl ? (
        <img
          src={file.thumbnailUrl}
          alt=""
          className="w-full h-full object-cover bg-muted"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xl">
          {iconForKind(kind)}
        </div>
      )}
      {onClick && overlay && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors">
          <span className="opacity-0 hover:opacity-100 text-white text-base">{overlay}</span>
        </span>
      )}
    </>
  )

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${wrapperBase} ring-border hover:ring-foreground/40 cursor-pointer`}
      >
        {inner}
      </button>
    )
  }
  return <div className={`${wrapperBase} ring-border/60`}>{inner}</div>
}

/**
 * ゲスト向け 画像 / PDF フルプレビュー モーダル。
 *
 * - 画像: ゲスト inline 署名 URL を `<img>` で表示
 * - PDF: 同 URL を `<iframe>` でブラウザ標準ビューア
 * - ウォーターマーク（共有URL token + ゲスト識別子）を重畳
 * - ESC / 背景クリックで閉じる
 */
function GuestPreviewModal({
  token,
  guestToken,
  file,
  onClose,
}: {
  token: string
  guestToken: string
  file: GuestFile | null
  onClose: () => void
}) {
  const kind = file ? classifyFile(file.mimeType) : 'other'
  const enabled = !!file && (kind === 'image' || kind === 'pdf')
  const { data, isLoading, error } = useQuery<{ url: string; expiresAt: string }, GuestApiError>({
    queryKey: ['guest-preview-url', token, file?.id ?? ''],
    queryFn: () => getGuestPreviewUrl(token, file?.id ?? '', guestToken),
    enabled,
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  })

  useEffect(() => {
    if (!file) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [file, onClose])

  if (!file) return null

  const watermarkLabel = `テラファイル便 · share/${token.slice(-6)} · g/${guestToken.slice(-6)}`

  return (
    <ModalOverlay
      className="bg-black/85"
      onBackdropClick={onClose}
      onEscape={onClose}
      aria-label={`${file.originalName} のプレビュー`}
    >
      <div className="fixed top-0 left-0 right-0 px-6 py-4 flex items-center justify-between text-white pointer-events-none z-[51]">
        <div className="min-w-0 flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
            Preview
          </span>
          <span className="text-sm font-medium truncate">{file.originalName}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="pointer-events-auto text-white/80 hover:text-white text-sm px-3 py-1.5 border border-white/30 rounded-md hover:bg-white/10 transition-colors"
        >
          閉じる (Esc)
        </button>
      </div>

      <div
        className="relative w-full max-w-6xl max-h-[88vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        {isLoading && <p className="text-white/70 text-sm">{LOADING.fetching}</p>}
        {error && (
          <p className="text-red-300 text-sm">プレビューの取得に失敗しました ({error.message})</p>
        )}
        {data?.url && kind === 'image' && (
          <div className="relative">
            <img
              src={data.url}
              alt={file.originalName}
              className="max-w-full max-h-[88vh] object-contain shadow-2xl bg-black/40"
            />
            <Watermark identifier={watermarkLabel} hint={file.originalName} />
          </div>
        )}
        {data?.url && kind === 'pdf' && (
          <div className="relative w-full">
            <iframe
              src={data.url}
              title={file.originalName}
              className="w-full h-[88vh] bg-white rounded-md shadow-2xl"
            />
            <Watermark identifier={watermarkLabel} hint={file.originalName} />
          </div>
        )}
        {!isLoading && !error && !['image', 'pdf'].includes(kind) && (
          <p className="text-white/70 text-sm">{USER_MESSAGES.previewNotSupported}</p>
        )}
      </div>
    </ModalOverlay>
  )
}

function GuestVideoPlayer({
  token,
  guestToken,
  file,
}: {
  token: string
  guestToken: string
  file: GuestFile
}) {
  // playback token は短命なので毎回再取得する（古いトークンを iframe に
  // 渡すと「コンテンツがブロックされました」になるためキャッシュさせない）
  const { data, error } = useQuery<StreamTokenOutput, GuestApiError>({
    queryKey: ['guest-stream-token', token, file.id],
    queryFn: () => getGuestStreamToken(token, file.id, guestToken),
    enabled: file.streamStatus === 'READY' && !!file.streamUid,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const lastReportedRef = useRef<number>(0)
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const d = event.data as { event?: string; currentTime?: number } | undefined | null
      if (!d || typeof d !== 'object' || !d.event) return
      const sec = Math.floor(d.currentTime ?? 0)
      if (d.event === 'timeupdate') {
        if (sec - lastReportedRef.current >= 30) {
          lastReportedRef.current = sec
          void postGuestPlaybackEvent(token, file.id, guestToken, {
            event: 'progress',
            playPositionSec: sec,
          })
        }
      } else if (d.event === 'ended') {
        void postGuestPlaybackEvent(token, file.id, guestToken, {
          event: 'end',
          playPositionSec: sec,
        })
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [token, file.id, guestToken])

  if (file.streamStatus !== 'READY') {
    return (
      <div className="aspect-video bg-muted flex items-center justify-center rounded-md">
        <p className="text-sm text-muted-foreground">{STREAM_STATUS_LABEL.PENDING}</p>
      </div>
    )
  }
  if (error) {
    return <p className="text-sm text-red-600">{error.message}</p>
  }
  if (!data) {
    return <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>
  }

  // ウォーターマーク用識別子: サービス名 + 共有URL token 末尾 + guestToken 末尾の合成。
  // サービス名で流出時のブランド露出、token/guest 末尾で追跡可能性を両立する。
  const watermarkLabel = `テラファイル便 · share/${token.slice(-6)} · g/${guestToken.slice(-6)}`

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-md border border-border">
      <iframe
        src={data.iframeUrl}
        title="動画プレーヤー"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        className="w-full h-full"
      />
      {/* ゲスト視聴用ウォーターマーク。token と viewerId を刻んで、流出時の追跡材料にする。 */}
      <Watermark identifier={watermarkLabel} hint={file.originalName} />
    </div>
  )
}

/**
 * ゲスト用動画プレイヤーのモーダル枠。中身は既存 GuestVideoPlayer を再利用。
 * 旧: 一覧上部のインライン展開 → 下のファイル再生時にスクロール戻しが発生して UX が悪い
 * 新: 中央モーダル + ESC / 背景クリックで閉じる
 */
function GuestVideoPlayerModal({
  token,
  guestToken,
  file,
  onClose,
}: {
  token: string
  guestToken: string
  file: GuestFile
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <ModalOverlay
      className="bg-black/85"
      onBackdropClick={onClose}
      onEscape={onClose}
      aria-label={`${file.originalName} の再生`}
    >
      <div className="fixed top-0 left-0 right-0 px-6 py-4 flex items-center justify-between text-white pointer-events-none z-[51]">
        <div className="min-w-0 flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
            Player
          </span>
          <span className="text-sm font-medium truncate">{file.originalName}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="pointer-events-auto text-white/80 hover:text-white text-sm px-3 py-1.5 border border-white/30 rounded-md hover:bg-white/10 transition-colors"
        >
          閉じる (Esc)
        </button>
      </div>

      <div
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <GuestVideoPlayer token={token} guestToken={guestToken} file={file} />
      </div>
    </ModalOverlay>
  )
}

function CenterLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      {children}
    </div>
  )
}

function permissionLabel(p: ShareLinkAccessOutput['permission']): string {
  return SHARE_PERMISSION_LABEL[p]
}

function streamStatusLabel(s: GuestFile['streamStatus']): string {
  // 動画ステータスのユーザー向け文言は labels.ts に集約 (issue #36)
  if (s === 'READY') return STREAM_STATUS_LABEL.READY
  if (s === 'ERROR') return STREAM_STATUS_LABEL.ERROR
  // PENDING / QUEUED / null (まだ動画でない等) は「準備中」扱い
  return STREAM_STATUS_LABEL.PENDING
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
